//@flow
import MediaTypeConstant from './MediaTypeConstant';
import { FuidRrnInfo, RepoItem } from '../autogenerated/ZoomBridge';
import _ from 'lodash';
import HashMap from './../protocol/HashMap';
import { FAV_ITEM_DETAIL_MAP } from '../store/Constants';
import Store from '../store/Store';
import CollectionUtil from '../queries/lightbox/CollectionUtil';

export default class FileInfoUtil {
  static FUID_BID_RRN_STRING_TEMPLATE: any;

  /**
   * Creates the new instance
   * @param  {number} fuid
   * @param  {number} bid
   * @param  {number} rrn
   * @returns {FuidRrnInfo}
   */
  static toFuidRrnInfo(fuid: number, bid: number, rrn: number) {
    return new FuidRrnInfo()
      .setFuid(fuid)
      .setBid(bid)
      .setRrn(rrn);
  }

  /**
   *
   * Creates a new fuid rrn info from repo item
   * @param repoItem{RepoItem}
   */
  static createFuidRrnInfo(repoItem: RepoItem) {
    return new FuidRrnInfo()
      .setFuid(repoItem.getFuid())
      .setRrn(repoItem.getRrn())
      .setBid(repoItem.getBid());
  }

  static createFuidBidRrnString(repoItem: RepoItem | FuidRrnInfo) {
    return FileInfoUtil.FUID_BID_RRN_STRING_TEMPLATE({
      fuid: repoItem.getFuid(),
      bid: repoItem.getBid(),
      rrn: repoItem.getRrn()
    });
  }

  static getMetadata(repoItem: RepoItem, key: string): ?string {
    if (repoItem) {
      const metadata: HashMap = repoItem.getMetadataMap();
      return metadata.get(key);
    } else {
      return undefined;
    }
  }

  static isAudioOrVideo(repoItem: RepoItem) {
    if (FileInfoUtil.isImageSequence(repoItem)) {
      return true;
    }
    const __mediaType = FileInfoUtil.getMediaType(repoItem);
    return MediaTypeConstant.AUDIO === __mediaType || MediaTypeConstant.VIDEO === __mediaType;
  }

  /**
   * Check an asset is Image Sequence Asset or not
   * @param repoItem
   * @return {boolean}
   */
  static isImageSequence(repoItem: RepoItem): boolean {
    if (FileInfoUtil.getMetadata(repoItem, 'ZPIG_Image Sequence') === 'true') {
      return true;
    }
    return false;
  }

  static isMultiPage(repoItem: RepoItem) {
    return MediaTypeConstant.MULTIPAGE === FileInfoUtil.getMediaType(repoItem);
  }

  static isAudio(repoItem: RepoItem) {
    return MediaTypeConstant.AUDIO === FileInfoUtil.getMediaType(repoItem);
  }

  static isVideo(repoItem: RepoItem) {
    return MediaTypeConstant.VIDEO === FileInfoUtil.getMediaType(repoItem);
  }

  static isImage(repoItem: RepoItem) {
    return _.includes([MediaTypeConstant.IMAGE, MediaTypeConstant.MULTIPAGE], FileInfoUtil.getMediaType(repoItem));
  }

  static isFolder(repoItem: RepoItem) {
    return repoItem.getFrn() <= 0;
  }

  static isProject(repoItem: RepoItem): boolean {
    return MediaTypeConstant.PROJECT === FileInfoUtil.getMediaType(repoItem);
  }

  static getFileSize(repoItem: RepoItem) {
    let length = 0;
    if (repoItem) {
      const fileAttributes = repoItem.getBasicFileAttributes();
      if (fileAttributes) {
        length = fileAttributes.getLength();
      }
    }
    return length;
  }

  static getMediaType(repoItem: RepoItem) {
    let mediaType = null;
    if (repoItem) {
      const fileAttributes = repoItem.getBasicFileAttributes();
      if (fileAttributes) {
        mediaType = fileAttributes.getMediaType();
      }
    }
    return mediaType;
  }

  static isFavAsset(fuid: number) {
    // return window.gs.fuid2FavItemDetailMap != null && window.gs.fuid2FavItemDetailMap.containsKey(fuid);
    const map: HashMap = Store.get(FAV_ITEM_DETAIL_MAP);
    return map && map.containsKey(fuid);
  }

  static getFileExtension(filename: string) {
    const __index = filename.lastIndexOf('.');
    if (__index > -1) return filename.substring(__index + 1);
    else return '';
  }

  //static ignoreFileTypes = ["app"];
  static ignoreFileNames = ['Thumbs.db', '.DS_Store'];

  /**
   * Creates a string with bid,fuid and rrn. This string can be used a key in a component or map.
   * This string has to be used as react key.
   * @param  {RepoItem} repoItem
   */
  static toBidFuidRrnString(repoItem: RepoItem | FuidRrnInfo, separator: string = ':') {
    //`${repoItem.getBid()}:${repoItem.getFuid()}:${repoItem.getRrn()}`
    return `${repoItem.getBid()}${separator}${repoItem.getFuid()}${separator}${repoItem.getRrn()}`;
  }

  /**
   * Creates a string with bid,fuid and rrn. This string can be used a key in a component or map.
   * This string has to be used as react key.
   * @param  {RepoItem} repoItem
   */
  static toBidFuidRrnFileNameString(repoItem: RepoItem, separator: string = ':') {
    //`${repoItem.getBid()}:${repoItem.getFuid()}:${repoItem.getRrn()}`
    return `${FileInfoUtil.toBidFuidRrnString(repoItem)}${separator}${repoItem.getName()}`;
  }

  static is3DAsset(asset: RepoItem) {
    const filename = asset ? asset.getName() : null;
    return FileInfoUtil.getFileExtension(filename || '').toLowerCase() === 'obj';
  }

  /**
   * This will only work on the collection asset metadata.
   * This won't work on collection metadata.
   * @param {RepoItem} repoItem
   */
  static getMyStatusOnCollection(repoItem: RepoItem): ?string {
    return this.getCollectionAssetProperty(repoItem, CollectionUtil.COLLECTION_PARAM_KEYS.MY_REVIEW_STATUS);
  }

  static getResolvedCommentsCounts(repoItem: RepoItem): number {
    const v = this.getCollectionAssetProperty(repoItem, CollectionUtil.COLLECTION_PARAM_KEYS.RESOLVED_COMMENTS_COUNTS);
    return v ? _.toNumber(v) : 0;
  }

  static getNumberOfRejections(repoItem: RepoItem): number {
    const v = this.getCollectionAssetProperty(repoItem, CollectionUtil.COLLECTION_PARAM_KEYS.NUMBER_OF_REJECTIONS);
    return v ? _.toNumber(v) : 0;
  }

  static getNumberOfApprovals(repoItem: RepoItem): number {
    const v = this.getCollectionAssetProperty(repoItem, CollectionUtil.COLLECTION_PARAM_KEYS.NUMBER_OF_APPROVALS);
    return v ? _.toNumber(v) : 0;
  }

  static getTotalComments(repoItem: RepoItem): number {
    const v = this.getCollectionAssetProperty(repoItem, CollectionUtil.COLLECTION_PARAM_KEYS.TOTAL_COMMENTS);
    return v ? _.toNumber(v) : 0;
  }

  static getUnreadCommentsCountForCollection(repoItem: RepoItem): number {
    const v = this.getCollectionAssetProperty(repoItem, CollectionUtil.COLLECTION_PARAM_KEYS.UNREAD_COMMENTS_COUNT);
    return v ? _.toNumber(v) : 0;
  }

  static getCollectionAssetProperty(repoItem: RepoItem, key: string): ?string {
    if (repoItem) {
      const metadata: HashMap = repoItem.getCollectionAssetProperties();
      return metadata.get(key);
    } else {
      return undefined;
    }
  }
}

export const AwaitSleep = function(waiting = 1000) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve(true);
    }, waiting);
  });
};
/**
 *
 * @type {Function|*}
 * @private
 */
FileInfoUtil.FUID_BID_RRN_STRING_TEMPLATE = _.template('fuid:<%=fuid%>:bid:<%=bid%>:rrn:<%=rrn%>');
